import {
  HttpUtilService,
  Logger,
  HttpClient,
  HttpResponse,
  HttpRequestInit,
  AgentSessionManager,
  ApiUriConstants,
} from '@nice-ccf/core-sdk';
import { DialPhoneRequest } from '../model/dial-phone-request';
import { SendDtmfRequest } from '../model/send-dtmf-request';

/**
 * Class to handling voice controls
 */
export class VoiceService {
  logger: Logger = new Logger('acd', 'VoiceService');
  protected utilService: HttpUtilService = new HttpUtilService();
  private agentSession: AgentSessionManager = <AgentSessionManager>{};

  /**
   * @example
   * ```
   * const voiceSvc = new VoiceService();
   * ```
   */
  constructor() {
    this.agentSession = AgentSessionManager.instance;
  }

  /**
   * Method to hold the active contact
   * @param contactId - Active contactId
   * @example
   * ```
   * holdContact(123);
   * ```
   */
  public holdContact(contactId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.HOLD_CONTACT_URI.replace('{sessionId}', sessionId).replace('{contactId}', contactId.toString());

    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('holdContact', 'hold contact success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('holdContact', 'hold contact failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to resume the holding contact
   * @param contactId - holding contactId
   * @example
   * ```
   * resumeContact(123);
   * ```
   */
  public resumeContact(contactId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.RESUME_CONTACT_URI.replace('{sessionId}', sessionId).replace('{contactId}', contactId.toString());

    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('resumeContact', 'resume contact success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('resumeContact', 'resume contact failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to mute the agent
   * @example
   * ```
   * muteAgent();
   * ```
   */
  public muteAgent(): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.MUTE_AGENT_URI.replace('{sessionId}', sessionId);

    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('muteAgent', 'mute agent success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('muteAgent', 'mute agent failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to unmute the agent
   * @example
   * ```
   * unmuteAgent();
   * ```
   */
  public unmuteAgent(): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.UNMUTE_AGENT_URI.replace('{sessionId}', sessionId);

    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('unmuteAgent', 'unmute agent success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('unmuteAgent', 'unmute agent failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to place an outbound call
   * @param contactDetails - it contains properties like phone no, skill id and parentContactId
   * @example -
   * ```
   * dialPhone(contactDetails);
   * ```
   */
  public dialPhone(dialPhoneRequest: DialPhoneRequest): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const url = baseUrl + ApiUriConstants.DIAL_PHONE_URI.replace('{sessionId}', sessionId);
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(authToken).headers,
      body: dialPhoneRequest,
    };

    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('dialPhone', 'call make success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('dialPhone', 'call make failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to send the dtmf
   * @param sendDtmfRequest - it contains properties like dtmf sequence, tone duration and tone spacing
   * @example
   * ```
   * sendDtmf(sendDtmfRequest);
   * ```
   */
  public sendDtmf(sendDtmfRequest:SendDtmfRequest): Promise<HttpResponse>{
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const url = baseUrl + ApiUriConstants.SEND_DTMF.replace('{sessionId}', sessionId);
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(authToken).headers,
      body: sendDtmfRequest,
    };

    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('sendDtmf', 'send dtmf success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('sendDtmf', 'send dtmf failed:-' + error.toString());
          reject(error);
        }
      );
    });
  };
  
  
  /**        
   * Method to mask a call
   * @param contactId - contact Id
   * @example -
   * ```
   * maskCall(contactId);
   * ```
   */
  public maskCall(contactId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.MASK_CALL_URI.replace('{sessionId}', sessionId).replace('{contactId}', contactId.toString());
    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('maskCall', 'mask call success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('maskCall', 'mask call failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to stop masking a call
   * @param contactId - contact Id
   * @example -
   * ```
   * unmaskCall(contactId);
   * ```
   */
  public unmaskCall(contactId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.UNMASK_CALL_URI.replace('{sessionId}', sessionId).replace('{contactId}', contactId.toString());
    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('unmaskCall', 'unmask call success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('unmaskCall', 'unmask call failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }
  /**
   * Method to make a consult to agent
   * @param agentId - agent id
   * @example
   * ```
   * consultAgent(12345)
   * ```
   */
  public consultAgent(agentId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const url = baseUrl + ApiUriConstants.CONSULT_AGENT_URI.replace('{sessionId}', sessionId);
    const targetAgentId = {
      targetAgentId: agentId.toString(),
    };
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(authToken).headers,
      body: targetAgentId,
    };

    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('consultAgent', 'consult agent call success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('consultAgent', 'consult agent call failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to record a call
   * @param contactId - contact Id
   * @example -
   * ```
   * recordCall(contactId);
   * ```
   */
  public recordCall(contactId: number): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const reqInit = this.utilService.initHeader(authToken);
    const url = baseUrl + ApiUriConstants.RECORD_CALL_URI.replace('{sessionId}', sessionId).replace('{contactId}', contactId.toString());
    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('recordCall', 'record call success');
          resolve(response);
        },
        (error: HttpResponse) => {
          this.logger.error('recordCall', 'record call failed :-' + error.toString());
          reject(error);
        }
      );
    });
  }
  /** Method to make the conference call
   * @example -
   * ```
   * conferenceCall();
   * ```
   */
  public conferenceCall(): Promise<HttpResponse> {
    const sessionId = this.agentSession.getSessionId();
    const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const url = baseUrl + ApiUriConstants.CONFERENCE_CALL_URI.replace('{sessionId}', sessionId);
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(authToken).headers,
    };
    return new Promise((resolve, reject) => {
      HttpClient.post(url, reqInit).then(
        (response: HttpResponse) => {
          this.logger.info('conferenceCall', 'conferenceCall success:-' + response.toString());
          resolve(response);
        },
        (error) => {
          this.logger.error('conferenceCall', 'conferenceCall failed:-' + error.toString());
          reject(error);
        }
      );
    });
  }

   /**
   * This method to make a call with agent
   * @param agentId - agent Id
   * @param parentContactId - existing call contact id
   * @example -
   * ```
   * dialAgent('23344','234234324');
   * ```
   */
    public dialAgent(agentId: string, parentContactId: string): Promise<HttpResponse>  {
      const sessionId = this.agentSession.getSessionId();
      const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
      const authToken = this.agentSession.auth.getAuthToken().accessToken;
      const url = baseUrl + ApiUriConstants.DIAL_AGENT_URI.replace('{sessionId}', sessionId);
      const payloadData = {
        targetAgentId: agentId,
        parentContactId: parentContactId
      };
  
      const reqInit: HttpRequestInit = {
        headers: this.utilService.initHeader(authToken).headers,
        body: payloadData,
      };
  
      return new Promise((resolve, reject) => {
        HttpClient.post(url, reqInit).then(
          (response: HttpResponse) => {
            this.logger.info('dialAgent', 'dial Agent success:-' + response.toString());
            resolve(response);
          },
          (error) => {
            this.logger.error('dialAgent', 'dial Agent failed:-' + error.toString());
            reject(error);
          }
        );
      });
    }
  
    /**
     * This method to make a call with another skill
     * @param skillId - skill Id
     * @example -
     * ```
     * dialSkill(23344);
     * ```
     */
     public dialSkill(skillId: number): Promise<HttpResponse>  {
      const sessionId = this.agentSession.getSessionId();
      const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
      const authToken = this.agentSession.auth.getAuthToken().accessToken;
      const url = baseUrl + ApiUriConstants.DIAL_SKILL_URI.replace('{sessionId}', sessionId);
      const payloadData = {
        skillId: skillId
      };
  
      const reqInit: HttpRequestInit = {
        headers: this.utilService.initHeader(authToken).headers,
        body: payloadData,
      };
  
      return new Promise((resolve, reject) => {
        HttpClient.post(url, reqInit).then(
          (response: HttpResponse) => {
            this.logger.info('dialSkill', 'dial Skill success:-' + response.toString());
            resolve(response);
          },
          (error) => {
            this.logger.error('dialSkill', 'dial Skill failed:-' + error.toString());
            reject(error);
          }
        );
      });
    }
    
    /**
     *  This method to transfer call
     * 
     *  @example -
     * ```
     * transferContact();
     * ```
     */
     public transferContact(): Promise<HttpResponse>  {
      const sessionId = this.agentSession.getSessionId();
      const baseUrl = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
      const url = baseUrl + ApiUriConstants.TRANSFER_CONTACT_URI.replace('{sessionId}', sessionId);
      const authToken = this.agentSession.auth.getAuthToken().accessToken;
      const reqInit = this.utilService.initHeader(authToken);
      return new Promise((resolve, reject) => {
        HttpClient.post(url, reqInit).then(
          (response: HttpResponse) => {
            this.logger.info('transferContact', 'transfer call contact success:-' + response.toString());
            resolve(response);
          },
          (error) => {
            this.logger.error('transferContact', 'transfer call contact failed:-' + error.toString());
            reject(error);
          }
        );
      });
    }
}
